import React, { useContext, useMemo, useState } from "react";
import * as Generators from "../../actions/generators";
import * as Constants from "../../utils/constants";
import { IDefaultHoursDay, IProjectResults, ITimeEntry, IUser, IWeekData } from "../../types/types";
import { moveOnTop, sortOn } from "../../utils/helpers";
import { IDropdownElement } from "../WeekTable";
import WeektableDateCell from "./WeektableDateCell";
import WeektableWeekTotalCell from "./WeektableWeekTotalCell";
import {
    DAY_EXPECTED_TOTAL,
    HIDE_COLUMN_BUTTON_STYLES,
    PROJECT_COLUMN_STYLES,
    WEEK_EXPECTED_TOTAL,
    WEEK_TOTAL_HEADER_STYLES,
} from "./utils";
import WeektableHideCell from "./WeektableHideCell";
import WeekTableProjectNameCell from "./WeekTableProjectNameCell";
import { ApiContext } from "../../providers/ApiProvider";
import AddProjectDialog from "../AddProjectDialog";
import QuickFillWeekDialog from "../QuickFillWeekDialog";
import MobileCardView from "./MobileCardView";
import { getFinnishHoliday } from "../../utils/finnishHolidays";
import { Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Button, Input, Tooltip } from "@fluentui/react-components";
import { Add24Regular, Search24Regular, Dismiss24Regular } from "@fluentui/react-icons";

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

interface IWeektableDataRowsProps {
    dbHours: ITimeEntry[];
    weekTotal: IWeekData["weekTotal"];
    field: IWeekData["dates"];
    dayTotal: { [x: string]: number };
    jwtoken: string;
    visibleProjects: string[] | undefined;
    selectedDays: number[];
    user: IUser;
    overflow: "auto" | "visible";
    projectsArr: string[];
    projectsData: IProjectResults[] | undefined;
    dropdownArr: IDropdownElement[];
    loadingCells: { [key: string]: boolean };
    setField: (field: IWeekData["dates"]) => void;
    fetchTimeEntries: (week: any[]) => Promise<void>;
    setVisibleProjects: (projects: string[] | undefined) => void;
    setProjectsData: (projects: IProjectResults[] | undefined) => void;
    setDropdownArr: (arr: IDropdownElement[] | undefined) => void;
    setLoadingCells: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
    update: () => void;
    focusedCellsRef: React.MutableRefObject<{ [key: string]: boolean }>;
    fieldRef: React.MutableRefObject<IWeekData["dates"] | undefined>;
    changedCells: { [key: string]: boolean };
    localChangesRef: React.MutableRefObject<{ [key: string]: string }>;
    onQuickFillSuccess: (updatedFieldNames?: string[]) => void;
    bulkFillOnSuccess?: (updatedFieldNames?: string[]) => void;
    defaultHoursByDate: { [date: number]: IDefaultHoursDay };
    defaultProjectIdByDate: { [date: number]: string | null };
    onDefaultHoursDayUpdated?: (day: IDefaultHoursDay) => void;
    showOnlyWithHours: boolean;
    setShowOnlyWithHours: (value: boolean) => void;
    /** Per-project month/year logged hours for the viewed period */
    projectHourTotals: { [projectId: string]: { monthHours: number; yearHours: number } };
    totalsPeriod: { year: number; month: number } | null;
}

export default function WeektableDataRows(props: IWeektableDataRowsProps) {
    const { updateUserAppSettings } = useContext(ApiContext);
    const [isSelectProjectDialogOpen, setIsSelectProjectDialogOpen] = useState(false);
    const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = useState(false);
    const [projectSearchQuery, setProjectSearchQuery] = useState("");
    const projectSearchInputRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
        if (isSelectProjectDialogOpen && projectSearchInputRef.current) {
            // Small delay to ensure dialog is fully rendered before focusing
            const id = window.setTimeout(() => {
                projectSearchInputRef.current?.focus();
            }, 0);
            return () => window.clearTimeout(id);
        }
        return;
    }, [isSelectProjectDialogOpen]);
    // Utility function to filter out the selected project from the dropdown array
    const filterDropdownArray = (arr: IDropdownElement[], project: IDropdownElement) => {
        return arr.filter((item) => item !== project);
    };

    // Handle project change event
    const handleProjectOnChange = async (project: IDropdownElement) => {
        if (!props.dropdownArr || !props.visibleProjects || !props.user?.app_settings) return;

        const newArr = filterDropdownArray(props.dropdownArr, project);
        const projectId = project.project_id;

        // Check that newItem is not already in array
        if (props.visibleProjects.includes(projectId)) return;

        const updatedVisibleProjects = [...props.visibleProjects, projectId];
        const appSettings = {
            ...props.user.app_settings,
            visible_projects: updatedVisibleProjects,
        };

        try {
            const user = await updateUserAppSettings(props.jwtoken, appSettings);
            props.setVisibleProjects(user.app_settings.visible_projects);
            props.setDropdownArr(newArr);
        } catch (error) {
            console.error("Failed to update user app settings", error);
        }
    };

    const groupedProjects = useMemo(() => {
        const groups: { [company: string]: IDropdownElement[] } = {};

        (props.dropdownArr || []).forEach((project) => {
            const company = project.label || "Other";
            if (!groups[company]) {
                groups[company] = [];
            }
            groups[company].push(project);
        });

        Object.keys(groups).forEach((key) => {
            groups[key].sort((a, b) => String(a.value).localeCompare(String(b.value)));
        });

        return groups;
    }, [props.dropdownArr]);

    const filteredGroups = useMemo(() => {
        if (!projectSearchQuery.trim()) {
            return groupedProjects;
        }

        const query = projectSearchQuery.toLowerCase();
        const filtered: { [company: string]: IDropdownElement[] } = {};

        Object.keys(groupedProjects).forEach((company) => {
            const matchingProjects = groupedProjects[company].filter(
                (project) => String(project.value).toLowerCase().includes(query) || company.toLowerCase().includes(query),
            );
            if (matchingProjects.length > 0) {
                filtered[company] = matchingProjects;
            }
        });

        return filtered;
    }, [groupedProjects, projectSearchQuery]);

    //This is called when Dialog component project list is updated.
    // This means that we should fetch new project list from database
    const handleNewProject = (data: any) => {
        console.log("Data from dialog", data);
        props.update();
    };

    // Generate new project row
    const generateProjectRow = (index: number) => {
        const row: any[] = [];
        let project: IProjectResults | null = null;
        if (props.projectsData) {
            project = props.projectsData[index];
        }
        if (project) {
            const companyName = project.company_name;
            const projectID = project.id!;
            const disHasHours = props.weekTotal[projectID] ? true : false;
            const projectName = project.project_name;

            // Determine if this project is the default project for at least one weekday in the current week
            const isDefaultInWeek = props.selectedDays.slice(1, 8).some((dayTimestamp) => {
                const dayOfWeek = new Date(dayTimestamp).getDay(); // 0 = Sunday, 6 = Saturday
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                if (isWeekend) return false;
                const configured = props.defaultProjectIdByDate[dayTimestamp];
                const effectiveDefaultProjectId = configured ?? Constants.DEFAULT_PROJECT_ID;
                return effectiveDefaultProjectId === projectID;
            });

            // Generate row if it has hours marked, is included in visibleProjects,
            // or is default on at least one weekday. NOTE: the base Default Hours project
            // is not forced visible when another project is default for all weekdays.
            if (disHasHours || props.visibleProjects?.includes(projectID) || isDefaultInWeek) {
                const isPrivate = project.private;
                const activeFrom = project.active.from;
                const activeTo = project.active.to;
                const isLocked = project.entry_locked;

                const textColor = isPrivate
                    ? "text-green-600"
                    : projectID === Constants.DEFAULT_PROJECT_ID
                      ? "text-red-600"
                      : "text-orange-500";

                const privText = isPrivate
                    ? "Private Project"
                    : projectID === Constants.DEFAULT_PROJECT_ID
                      ? "Default Project (Allways visible)"
                      : "Public Project";

                const projectTotals = props.projectHourTotals[projectID];
                const monthLabel = props.totalsPeriod
                    ? `${MONTH_NAMES[props.totalsPeriod.month - 1]} ${props.totalsPeriod.year}`
                    : undefined;
                const yearLabel = props.totalsPeriod ? String(props.totalsPeriod.year) : undefined;
                const monthHours = props.totalsPeriod ? (projectTotals?.monthHours ?? 0) : undefined;
                const yearHours = props.totalsPeriod ? (projectTotals?.yearHours ?? 0) : undefined;

                // let hasDescription: string | null = null;

                // First cell of the row (company name & project name)
                const styles = { ...PROJECT_COLUMN_STYLES, overflow: "scroll" }; // Create a copy with overflow: scroll
                row.push({
                    key: projectID,
                    content: (
                        <WeekTableProjectNameCell
                            projectName={projectName}
                            companyName={companyName}
                            textColor={textColor}
                            privText={privText}
                            projectId={projectID}
                            isLocked={isLocked}
                            isDefaultInWeek={isDefaultInWeek}
                            note={project.note}
                            monthHours={monthHours}
                            yearHours={yearHours}
                            monthLabel={monthLabel}
                            yearLabel={yearLabel}
                        />
                    ),
                    hashours: disHasHours.toString(), // With this flag we control which projects are to be seen. (Automatically shown if hours)
                    styles: styles,
                });

                // Monday-Sunday cells (1-7) for each project (0: previous week last day, 1-7: current week, 8: next week first day)
                for (let i = 1; i < 8; i++) {
                    const date = props.selectedDays[i];
                    const fieldName = projectID + "_" + date;

                    let locked = isLocked;
                    let placeholder = "";
                    if (!locked) {
                        if (activeFrom) {
                            if (!(activeFrom <= date.valueOf())) {
                                locked = true;
                            }
                        }
                        if (activeTo) {
                            if (!(date.valueOf() <= activeTo)) {
                                locked = true;
                            }
                        }
                    }
                    if (locked) {
                        if (projectID === Constants.DEFAULT_PROJECT_ID) {
                            placeholder = "";
                        } else placeholder = "locked";
                    }

                    const configuredDefaultProjectId = props.defaultProjectIdByDate[date];
                    const effectiveDefaultProjectId = configuredDefaultProjectId ?? Constants.DEFAULT_PROJECT_ID;
                    const dayOfWeek = new Date(date).getDay(); // 0 = Sunday, 6 = Saturday
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    const isDefaultForDay = !isWeekend && projectID === effectiveDefaultProjectId;

                    // If default hours day is not configured, check if there is a time entry for this project and date.
                    // This is being used for selecting the right color indicator in the cell.
                    const isManualDefaultDay =
                        props.defaultHoursByDate[date]?.mode === "manual" ||
                        (props.defaultHoursByDate[date]?.mode === undefined &&
                            props.dbHours.some((hour) => hour.from === date && hour.project_id === projectID));

                    /** Date cells */
                    row.push({
                        key: `${projectID}-${i}`,
                        truncateContent: true,
                        styles: {
                            minWidth: "80px",
                            maxWidth: "80px",
                            marginRight: 0,
                        },
                        content: (
                            <WeektableDateCell
                                field={props.field}
                                projectID={projectID}
                                fieldName={fieldName}
                                dbHours={props.dbHours}
                                date={date}
                                locked={locked}
                                jwtoken={props.jwtoken}
                                loadingCells={props.loadingCells}
                                placeholder={placeholder}
                                project={project}
                                selectedDays={props.selectedDays}
                                user={props.user}
                                setField={props.setField}
                                setLoadingCells={props.setLoadingCells}
                                fetchTimeEntries={props.fetchTimeEntries}
                                setProjectsData={props.setProjectsData}
                                projectsData={props.projectsData}
                                focusedCellsRef={props.focusedCellsRef}
                                fieldRef={props.fieldRef}
                                changedCells={props.changedCells}
                                localChangesRef={props.localChangesRef}
                                isDefaultForDay={isDefaultForDay}
                                // Manual mode is tracked per day; the cell component decides
                                // whether to show the indicator based on whether this project
                                // is the effective default for that day.
                                isManualDefaultDay={isManualDefaultDay || undefined}
                                onDefaultHoursDayUpdated={props.onDefaultHoursDayUpdated}
                            />
                        ),
                    });
                }

                /** Cell for total hours */
                row.push({
                    key: `${projectID}-total`,
                    content: <WeektableWeekTotalCell projectID={projectID} weekTotal={props.weekTotal} />,
                    truncateContent: true,
                    styles: WEEK_TOTAL_HEADER_STYLES,
                });

                /** Cell for hide icon eye */
                row.push({
                    key: `${projectID}-hide`,
                    content:
                        projectID === Constants.DEFAULT_PROJECT_ID ? null : (
                            <WeektableHideCell
                                projectId={projectID}
                                disHasHours={disHasHours}
                                projectName={projectName}
                                jwtoken={props.jwtoken}
                                user={props.user}
                                visibleProjects={props.visibleProjects}
                                update={props.update}
                                setVisibleProjects={props.setVisibleProjects}
                                updateUserAppSettings={updateUserAppSettings}
                            />
                        ),
                    styles: HIDE_COLUMN_BUTTON_STYLES,
                });
            }
        }
        return row;
    };

    // This collects all project rows and generate row object for table
    const generateRows = () => {
        // console.log("generateRows called with projectsData:", props.projectsData);
        let rows: any[] = [];
        const projectsToBeSeenArr: any[] = [];

        // Determine the admin-selected default project for the current week:
        // first non-DEFAULT_PROJECT_ID project that is configured as default for at least one weekday.
        const days = props.selectedDays.slice(1, 8);
        let adminDefaultProjectId: string | null = null;
        for (const dayTimestamp of days) {
            const dayOfWeek = new Date(dayTimestamp).getDay(); // 0 = Sunday, 6 = Saturday
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            if (isWeekend) continue;
            const configured = props.defaultProjectIdByDate[dayTimestamp];
            const effectiveDefaultProjectId = configured ?? Constants.DEFAULT_PROJECT_ID;
            if (effectiveDefaultProjectId && effectiveDefaultProjectId !== Constants.DEFAULT_PROJECT_ID) {
                adminDefaultProjectId = effectiveDefaultProjectId;
                break;
            }
        }

        // Generate rows based on array of projects found
        rows = (props.projectsData || []).map((project, index) => {
            const projectRow = generateProjectRow(index);
            return {
                // Use project name for sorting so desktop and mobile views share the same order
                key: `${project.project_name}_${project.id}`,
                items: projectRow,
                styles: { height: "auto" },
            };
        });
        // console.log("Generated rows:", rows);

        sortOn(rows, "key"); //Sort rows based on project name

        moveOnTop(rows, Constants.DEFAULT_PROJECT_ID); //Move default project on top

        // Check if project has hours set. If so add it to rows, otherwise skip.
        // Added also check if user selected project to be seen even without hours.
        // Always force visibility for any project that is the effective default
        // for at least one weekday in the current week.
        rows.forEach((row) => {
            if (row.items.length !== 0) {
                const projectID = row.key.split("_").pop(); // Get projectID from row key (handles underscores in name)

                // Determine if this project is the effective default for at least one weekday in the current week
                const isDefaultAnyWeekday = days.some((dayTimestamp) => {
                    const dayOfWeek = new Date(dayTimestamp).getDay(); // 0 = Sunday, 6 = Saturday
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    if (isWeekend) return false;
                    const configured = props.defaultProjectIdByDate[dayTimestamp];
                    const effectiveDefaultProjectId = configured ?? Constants.DEFAULT_PROJECT_ID;
                    return effectiveDefaultProjectId === projectID;
                });

                const hasHours = row.items[0].hashours === "true";
                const isVisibleByUserPreference = props.visibleProjects?.includes(projectID);
                const isBaseDefaultProject = projectID === Constants.DEFAULT_PROJECT_ID;

                // When "show only projects with hours" is enabled, hide projects
                // that have no hours and are not defaults. We still keep:
                // - the base Default Hours project
                // - any project that is default for at least one weekday in the current week
                if (props.showOnlyWithHours) {
                    if (hasHours || isDefaultAnyWeekday || isBaseDefaultProject) {
                        projectsToBeSeenArr.push(row);
                    }
                } else {
                    // Default behavior: show projects that either have hours,
                    // were explicitly selected to be visible, or are default.
                    if (hasHours || isVisibleByUserPreference || isDefaultAnyWeekday) {
                        projectsToBeSeenArr.push(row);
                    }
                }
            }
        });
        // console.log("Final projectsToBeSeenArr:", projectsToBeSeenArr);

        if (adminDefaultProjectId) {
            const targetIndex = projectsToBeSeenArr.findIndex((row) => row.key && row.key.endsWith(`_${adminDefaultProjectId}`));
            if (targetIndex > 0) {
                const [adminRow] = projectsToBeSeenArr.splice(targetIndex, 1);

                // Try to place after the Default Hours row when it exists,
                // otherwise move admin default to the very top.
                const defaultRowIndex = projectsToBeSeenArr.findIndex(
                    (row) => row.key && row.key.endsWith(`_${Constants.DEFAULT_PROJECT_ID}`),
                );

                if (defaultRowIndex !== -1) {
                    projectsToBeSeenArr.splice(defaultRowIndex + 1, 0, adminRow);
                } else {
                    projectsToBeSeenArr.splice(0, 0, adminRow);
                }
            }
        }

        // Select project row at the bottom of the weektable
        projectsToBeSeenArr.push({
            key: "selectProjectRow",
            items: Generators.generateSelectProject(props.dropdownArr, props.user!, props.jwtoken, handleProjectOnChange, handleNewProject),
        });

        // Day total cell row at the bottom of the weektable
        projectsToBeSeenArr.push({
            key: "dayTotalCell",
            items: Generators.generateDayTotalCell(props.selectedDays, props.dayTotal, props.weekTotal),
        });

        return projectsToBeSeenArr;
    };

    // console.log("WeektableDataRows render:", {
    //     selectedDays: props.selectedDays,
    //     projectsArr: props.projectsArr,
    //     dropdownArr: props.dropdownArr,
    //     visibleProjects: props.visibleProjects,
    //     user: props.user,
    // });

    const isMobile = props.overflow === "auto";

    // Determine which weekday column (if any) represents "today" in the current week view.
    // Index is 0–6 for Monday–Sunday slice, or -1 when today is not in the visible week.
    const today = new Date();
    const todayColumnIndex = props.selectedDays.slice(1, 8).findIndex((day) => new Date(day).toDateString() === today.toDateString());

    return (
        <div
            className={`w-full max-w-7xl mx-auto text-(--colorNeutralForeground1) ${
                props.overflow === "auto" ? "overflow-auto" : "overflow-visible"
            }`}
        >
            {isMobile ? (
                <MobileCardView
                    generateRows={generateRows}
                    projectsData={props.projectsData}
                    weekTotal={props.weekTotal}
                    selectedDays={props.selectedDays}
                    dayTotal={props.dayTotal}
                    dropdownArr={props.dropdownArr!}
                    user={props.user}
                    jwtoken={props.jwtoken}
                    handleProjectOnChange={handleProjectOnChange}
                    handleNewProject={handleNewProject}
                    onQuickFillSuccess={props.onQuickFillSuccess}
                    field={props.field}
                    dbHours={props.dbHours}
                    setField={props.setField}
                    loadingCells={props.loadingCells}
                    setLoadingCells={props.setLoadingCells}
                    fetchTimeEntries={props.fetchTimeEntries}
                    setProjectsData={props.setProjectsData}
                    focusedCellsRef={props.focusedCellsRef}
                    fieldRef={props.fieldRef}
                    changedCells={props.changedCells}
                    localChangesRef={props.localChangesRef}
                    visibleProjects={props.visibleProjects}
                    setVisibleProjects={props.setVisibleProjects}
                    update={props.update}
                    updateUserAppSettings={updateUserAppSettings}
                    bulkFillOnSuccess={props.bulkFillOnSuccess}
                    showOnlyWithHours={props.showOnlyWithHours}
                    setShowOnlyWithHours={props.setShowOnlyWithHours}
                    defaultProjectIdByDate={props.defaultProjectIdByDate}
                    projectHourTotals={props.projectHourTotals}
                    totalsPeriod={props.totalsPeriod}
                />
            ) : (
                /* Desktop Table View */
                <div className="w-full border-collapse overflow-x-auto md:overflow-x-visible">
                    {/* Header Row */}
                    <div className="flex border-b border-gray-200 dark:border-gray-600 min-w-max md:min-w-0 md:w-full bg-(--rs-bg)">
                        {/* Project Column - Fixed width for mobile, flexible for desktop */}
                        <div
                            className="w-32 sm:w-40 md:w-40 lg:w-52 min-w-[128px] sm:min-w-[160px] md:min-w-0 md:shrink-0 px-2 py-2 text-left text-sm flex items-center sticky left-0 bg-(--rs-bg) text-(--colorNeutralForeground1) z-10 md:static md:z-0"
                            title="Hover a project to see your month and year totals"
                        >
                            PROJECTS
                        </div>
                        {/* Day Columns - Fixed width on mobile, flexible on desktop */}
                        {props.selectedDays.slice(1, 8).map((day, index) => {
                            const dateObj = new Date(day);
                            const weekdayName = dateObj.toLocaleDateString("en-FI", {
                                weekday: "long",
                            });
                            const dateString = dateObj.toLocaleDateString("en-FI", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "2-digit",
                            });

                            // Check if this is today (matches the index computed above)
                            const isToday = index === todayColumnIndex;
                            const holiday = getFinnishHoliday(dateObj);
                            const isHoliday = !!holiday;

                            return (
                                <div
                                    key={index}
                                    className={`w-20 sm:w-20 md:flex-[1_1_0%] md:min-w-0 min-w-[80px] sm:min-w-[80px] px-1 py-2 text-center text-[0.7rem] sm:text-xs border-b ${
                                        isHoliday
                                            ? "bg-(--colorNeutralBackground2) border-(--colorNeutralStroke2)"
                                            : "bg-(--rs-bg) border-transparent"
                                    } ${isToday ? "bg-blue-50 dark:bg-blue-900/25" : "text-(--colorNeutralForeground1)"}`}
                                    title={holiday ? holiday.name : undefined}
                                >
                                    <div className="flex flex-col items-center justify-center gap-0 leading-tight">
                                        <div className={`wrap-break-word font-medium ${isToday ? "underline today-header-text" : ""}`}>
                                            {weekdayName}
                                        </div>
                                        <div className={`wrap-break-word text-[0.6rem] sm:text-xs ${isToday ? "today-header-text" : ""}`}>
                                            {dateString}
                                        </div>
                                        {holiday && (
                                            <div className="mt-0.5 text-[0.6rem] sm:text-[0.65rem] font-semibold text-(--colorNeutralForeground2)">
                                                {holiday.name}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {/* Quick Fill Header Column */}
                        <div className="w-16 sm:w-16 md:w-20 min-w-[64px] sm:min-w-[64px] px-1 py-2 text-center text-xs sm:text-sm font-bold flex items-center justify-center bg-(--rs-bg) text-(--colorNeutralForeground1)">
                            {/* Empty header for Fill button column */}
                        </div>
                        {/* Week Total Column */}
                        <div className="w-16 sm:w-20 md:w-24 lg:w-32 min-w-[64px] sm:min-w-[80px] md:min-w-0 md:shrink-0 px-1 py-2 text-center text-xs sm:text-sm font-bold flex items-center justify-center bg-(--rs-bg) text-(--colorNeutralForeground1)">
                            Total
                        </div>
                        {/* Hide Icon Column */}
                        <div className="w-12 min-w-[48px] md:shrink-0 px-1 py-2 text-center text-xs sm:text-sm font-bold flex items-center justify-center bg-(--rs-bg) text-(--colorNeutralForeground1)">
                            {/* Hide icon header - empty for now */}
                        </div>
                    </div>

                    {/* Project Rows */}
                    {generateRows().map((row: any, index: number) => {
                        if (row.key === "dayTotalCell" || row.key === "selectProjectRow") {
                            return null; // Skip these for now, handle separately
                        }

                        return (
                            <div
                                key={row.key || index}
                                className="group flex border-b h-13 border-gray-200 dark:border-gray-700 min-w-max md:min-w-0 md:w-full hover:bg-(--colorNeutralBackground1Hover) transition-colors duration-150"
                            >
                                {/* Project Name Column - Fixed width on mobile, larger on desktop */}
                                <div className="w-32 sm:w-40 md:w-40 lg:w-52 min-w-[128px] sm:min-w-[160px] md:min-w-0 md:shrink-0 px-2 py-1 flex items-center overflow-hidden sticky left-0 bg-(--rs-bg) hover:bg-(--rs-option-hover) z-10 md:static md:z-0 transition-colors duration-150">
                                    <div className="w-full truncate">{row.items[0]?.content}</div>
                                </div>
                                {/* Day Data Columns - Fixed width on mobile, flexible on desktop */}
                                {row.items.slice(1, -2).map((cell: any, cellIndex: number) => {
                                    const isTodayCell = cellIndex === todayColumnIndex;
                                    return (
                                        <div
                                            key={cellIndex}
                                            className={`w-20 sm:w-20 md:flex-[1_1_0%] md:min-w-0 min-w-[80px] sm:min-w-[80px] px-1 py-2 flex items-center justify-center transition-colors duration-150 ${
                                                isTodayCell
                                                    ? "bg-blue-50 dark:bg-blue-900/20"
                                                    : "group-hover:bg-(--colorNeutralBackground1Hover)"
                                            }`}
                                        >
                                            {cell.content}
                                        </div>
                                    );
                                })}
                                {/* Quick Fill Button Column */}
                                {(() => {
                                    const projectID = row.key.split("_")[1]; // Extract project ID from row key
                                    const project = props.projectsData?.find((p) => p.id === projectID);
                                    if (project) {
                                        return (
                                            <div className="w-16 sm:w-16 md:w-20 min-w-[64px] sm:min-w-[64px] px-1 py-1 flex items-center justify-center group-hover:bg-(--colorNeutralBackground1Hover) transition-colors duration-150">
                                                <QuickFillWeekDialog
                                                    token={props.jwtoken}
                                                    user={props.user}
                                                    projectId={projectID}
                                                    projectName={project.project_name}
                                                    selectedDays={props.selectedDays}
                                                    onSuccess={props.onQuickFillSuccess}
                                                />
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                                {/* Week Total Column */}
                                <div className="w-16 sm:w-20 md:w-24 lg:w-32 min-w-[64px] sm:min-w-[80px] md:min-w-0 md:shrink-0 px-1 py-1 flex items-center justify-center group-hover:bg-(--colorNeutralBackground1Hover) transition-colors duration-150 text-orange-500">
                                    {row.items[row.items.length - 2]?.content}
                                </div>
                                {/* Hide Icon Column */}
                                <div className="w-12 min-w-[48px] md:shrink-0 px-1 py-1 flex items-center justify-center group-hover:bg-(--colorNeutralBackground1Hover) transition-colors duration-150">
                                    {row.items[row.items.length - 1]?.content}
                                </div>
                            </div>
                        );
                    })}

                    {/* Day Total Row */}
                    <div className="flex h-10 min-w-max md:min-w-0 md:w-full">
                        {/* Day Total Label - Fixed width */}
                        <div className="w-32 sm:w-40 md:w-40 lg:w-52 min-w-[128px] sm:min-w-[160px] md:min-w-0 md:shrink-0 px-2 py-1 flex items-center font-semibold">
                            Day Total
                        </div>
                        {/* Day Total Data Columns - Flexible on desktop */}
                        {props.selectedDays.slice(1, 8).map((day, index) => {
                            const dayTotalValue = props.dayTotal[day.toString()] ?? 0;
                            const dayTotalColor =
                                dayTotalValue >= DAY_EXPECTED_TOTAL
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-red-600 dark:text-red-400";
                            const isToday = index === todayColumnIndex;
                            return (
                                <div
                                    key={index}
                                    className={`w-20 sm:w-20 md:flex-[1_1_0%] md:min-w-0 min-w-[80px] sm:min-w-[80px] px-1 py-1 flex itemsCenter justify-center font-semibold ${
                                        isToday ? "bg-blue-50 dark:bg-blue-900/20" : ""
                                    }`}
                                >
                                    <div
                                        className={`w-20 sm:w-20 md:flex-[1_1_0%] md:min-w-0 min-w-[80px] sm:min-w-[80px] px-1 py-1 flex items-center justify-center font-semibold ${dayTotalColor}`}
                                    >
                                        {dayTotalValue.toFixed(1)}
                                    </div>
                                </div>
                            );
                        })}
                        {/*Empty Day Total Column*/}
                        <div className="w-16 sm:w-16 md:w-20 min-w-[64px] sm:min-w-[64px] px-1 py-2 text-center text-xs sm:text-sm font-bold flex items-center justify-center bg-(--rs-bg) text-(--colorNeutralForeground1)">
                            {/* Empty day total column */}
                        </div>
                        {/* Week Total Column */}
                        <div className="w-16 sm:w-20 md:w-24 lg:w-32 min-w-[64px] sm:min-w-[80px] md:min-w-0 md:shrink-0 px-1 py-1 flex items-center justify-center">
                            {/* Calculate and display week total */}
                            {(() => {
                                const weekTotalValue = Object.values(props.weekTotal).reduce((acc, curr) => acc + curr, 0) ?? 0;
                                const weekTotalColor =
                                    weekTotalValue >= WEEK_EXPECTED_TOTAL
                                        ? "text-green-600 dark:text-green-400"
                                        : "text-red-600 dark:text-red-400";
                                return (
                                    <div
                                        className={`w-16 sm:w-20 md:w-24 lg:w-32 min-w-[64px] sm:min-w-[80px] md:min-w-0 md:shrink-0 px-1 py-1 flex items-center justify-center font-semibold ${weekTotalColor}`}
                                    >
                                        {weekTotalValue.toFixed(1)}
                                    </div>
                                );
                            })()}
                        </div>
                        {/* Empty Hide Icon Column */}
                        <div className="w-12 min-w-[48px] md:shrink-0 px-1 py-1 flex items-center justify-center">{/* Empty */}</div>
                    </div>

                    {/* Select Project Row / Desktop add-project dialog */}
                    {props.dropdownArr && props.dropdownArr.length > 0 && (
                        <>
                            <div className="mt-3 flex justify-start">
                                <Button
                                    appearance="secondary"
                                    icon={<Add24Regular />}
                                    onClick={() => {
                                        setIsSelectProjectDialogOpen(true);
                                        setProjectSearchQuery("");
                                    }}
                                >
                                    Add project…
                                </Button>
                            </div>

                            <Dialog
                                open={isSelectProjectDialogOpen}
                                onOpenChange={(_, data) => {
                                    setIsSelectProjectDialogOpen(data.open);
                                    if (!data.open) {
                                        setProjectSearchQuery("");
                                    }
                                }}
                            >
                                {/* Wide dialog constrained to viewport; inner list scrolls */}
                                <DialogSurface
                                    className="flex flex-col overflow-hidden"
                                    style={{
                                        width: "90vw",
                                        maxWidth: "1100px",
                                        height: "90vh",
                                    }}
                                >
                                    <DialogBody className="flex flex-col h-full w-full overflow-hidden p-0">
                                        <div className="flex flex-col gap-3 mb-4 w-full">
                                            {/* Dialog Title and Close Button ROW 1*/}
                                            <div className="flex flex-row items-center w-full gap-2">
                                                <DialogTitle className="text-lg font-semibold text-(--colorNeutralForeground1) flex-1 min-w-0">
                                                    Select project to add
                                                </DialogTitle>
                                                <DialogTrigger disableButtonEnhancement>
                                                    <Button
                                                        appearance="subtle"
                                                        icon={<Dismiss24Regular />}
                                                        onClick={() => {
                                                            setIsSelectProjectDialogOpen(false);
                                                            setProjectSearchQuery("");
                                                        }}
                                                        className="min-h-[32px] min-w-[32px] p-0 shrink-0 rounded-full"
                                                        aria-label="Close dialog"
                                                    />
                                                </DialogTrigger>
                                            </div>

                                            {/* Search Input and Create Project Button ROW 2*/}
                                            <div className="flex flex-col sm:flex-row w-full sm:items-center gap-2 sm:gap-3">
                                                {/* Search Input */}
                                                <div className="relative flex-1 min-w-0">
                                                    <Input
                                                        ref={projectSearchInputRef}
                                                        placeholder="Search projects…"
                                                        value={projectSearchQuery}
                                                        onChange={(e) => {
                                                            const target = e.target as HTMLInputElement;
                                                            setProjectSearchQuery(target.value);
                                                        }}
                                                        className="w-full h-10 text-sm"
                                                    />
                                                </div>
                                                {/* Create Project Button */}
                                                <Button
                                                    appearance="primary"
                                                    icon={<Add24Regular />}
                                                    className="w-full sm:w-auto sm:ml-auto sm:self-stretch"
                                                    onClick={() => {
                                                        setIsSelectProjectDialogOpen(false);
                                                        setProjectSearchQuery("");
                                                        setIsCreateProjectDialogOpen(true);
                                                    }}
                                                >
                                                    <span className="hidden sm:inline">Create new project</span>
                                                    <span className="sm:hidden">Create</span>
                                                </Button>
                                            </div>
                                        </div>

                                        <DialogContent className="flex-1 min-h-0 overflow-y-auto px-5 pb-4">
                                            {Object.keys(filteredGroups).length === 0 ? (
                                                <div className="px-4 py-6 text-center">
                                                    <p className="text-sm text-(--colorNeutralForeground2)">
                                                        {projectSearchQuery
                                                            ? "No projects found matching your search"
                                                            : "No projects available"}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="divide-y divide-gray-200 dark:divide-gray-600">
                                                    {Object.keys(filteredGroups)
                                                        .sort()
                                                        .map((company) => (
                                                            <div key={company} className="py-0.5">
                                                                <div className="px-5 py-1.5 text-[0.7rem] font-semibold text-(--colorNeutralForeground1) uppercase tracking-wide bg-(--colorNeutralBackground6)">
                                                                    {company}
                                                                </div>
                                                                <div>
                                                                    {filteredGroups[company].map((project) => {
                                                                        const stableKey =
                                                                            project.project_id ||
                                                                            project.key ||
                                                                            `${company}-${String(project.value)}`;

                                                                        const content = (
                                                                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                                                <div
                                                                                    className={`text-sm font-medium leading-tight ${
                                                                                        project.private
                                                                                            ? "text-green-600 dark:text-green-400"
                                                                                            : "text-orange-500"
                                                                                    }`}
                                                                                >
                                                                                    {project.value}
                                                                                </div>
                                                                                {project.label && (
                                                                                    <div className="text-xs text-(--colorNeutralForeground2) mt-0.5 leading-tight truncate">
                                                                                        {project.label}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );

                                                                        const buttonInner = project.note ? (
                                                                            <Tooltip content={project.note} relationship="description">
                                                                                {content}
                                                                            </Tooltip>
                                                                        ) : (
                                                                            content
                                                                        );

                                                                        return (
                                                                            <button
                                                                                key={stableKey}
                                                                                onClick={() => {
                                                                                    handleProjectOnChange(project);
                                                                                    setIsSelectProjectDialogOpen(false);
                                                                                    setProjectSearchQuery("");
                                                                                }}
                                                                                className="w-full px-5 py-2.5 text-left hover:bg-(--colorNeutralBackground1Hover) active:bg-(--colorNeutralBackground1Pressed) transition-colors touch-manipulation min-h-[48px] flex items-center"
                                                                            >
                                                                                {buttonInner}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </DialogContent>
                                    </DialogBody>
                                </DialogSurface>
                            </Dialog>

                            {isCreateProjectDialogOpen && (
                                <AddProjectDialog
                                    dropdownArr={props.dropdownArr}
                                    user={props.user}
                                    token={props.jwtoken}
                                    initialOpen={isCreateProjectDialogOpen}
                                    onOpenChange={(open) => {
                                        setIsCreateProjectDialogOpen(open);
                                    }}
                                    handleSelectedProject={(project) => {
                                        handleProjectOnChange(project);
                                        setIsCreateProjectDialogOpen(false);
                                    }}
                                    passChildData={(data) => {
                                        handleNewProject(data);
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
